/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	Text,
	Flex,
	Stack,
	Box,
	Button,
	ActionIcon,
	Skeleton,
} from '@mantine/core';
import classes from './MetricCard.module.css';
import TotalOffsetLogo from '@assets/images/totalOffset.png';
import EmissionOffsetLogo from '@assets/images/emissionOffset.png';
import AddNewContributionLogo from '@assets/images/add-new-contribution.svg';

import {
	IconCirclePlus,
	IconPlus,
} from '@tabler/icons-react';
import { getCurrencyUnit } from '@utils/currency-units';
import { getUnitLabel } from '@utils/units';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSpaces } from '@utils/formats';

type TMetricCardProps = {
	type:
		| 'addOffset'
		| 'emissionsCompensated'
		| 'totalCompensation'
		| 'addAShortcut';
	title?: string;
	value?: number;
	isLoading: boolean;
	onClick?: () => void;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MetricCard = (props: TMetricCardProps) => {
	const { t } = useTranslation();
	const { title, value, type, isLoading, onClick } =
		props;
	/* ***************************** RENDER HELPERS ***************************** */
	if (type == 'addOffset')
		return (
			<Box className={classes.containerAddOffset}>
				<Box className={classes.containerLeftAddOffset}>
					<img
						src={AddNewContributionLogo}
						className={classes.addNewContribution}
					/>
				</Box>
				<Box className={classes.containerRightAddOffset}>
					<Text
						className={classes.textAddNewContribution}
					>
						{title}
					</Text>
					<Button
						onClick={onClick}
						color="white"
						className={classes.buttonAddNewContribution}
						leftSection={<IconCirclePlus size={23} />}
					>
						{t('global.start')}
					</Button>
				</Box>
			</Box>
		);

	if (type == 'addAShortcut')
		return (
			<Box className={classes.containerAddShortcut}>
				<Stack gap={'14px'} align="center">
					<ActionIcon
						disabled
						variant="filled"
						aria-label="Settings"
						size={'57px'}
						style={{
							backgroundColor: '#E4E7FF',
						}}
					>
						<IconPlus
							style={{ width: '70%', height: '70%' }}
							stroke={1.5}
							color="#647fe8"
						/>
					</ActionIcon>
					<Text className={classes.textAddShortcut}>
						{t('global.addAShortcut')}
					</Text>
				</Stack>
			</Box>
		);
	/* ******************************** RENDERING ******************************* */
	return (
		<Stack gap={'32px'} className={classes.container}>
			<Flex
				align={'center'}
				gap={'18px'}
				className={classes.header}
			>
				<img
					src={
						type == 'emissionsCompensated'
							? EmissionOffsetLogo
							: TotalOffsetLogo
					}
					className={classes.icon}
				/>
				<Text className={classes.title}>{title}</Text>
			</Flex>
			<Flex className={classes.body}>
				{isLoading ? (
					<Skeleton
						mt={'xs'}
						height={'40px'}
						width={'150px'}
					/>
				) : (
					<>
						<Text lineClamp={1} className={classes.value}>
							{formatNumberWithSpaces(
								Number(value?.toFixed(3) || 0)
							)}
						</Text>
						<Text className={classes.value}>
							{type == 'emissionsCompensated'
								? getUnitLabel('tco2e')
								: getCurrencyUnit('EUR')}
						</Text>
					</>
				)}
			</Flex>
		</Stack>
	);
};

export default MetricCard;
